import React, { useEffect, useState } from 'react';
import { Home, Building2, Info } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../../ui/alert';
import SurveyNumberStepper from './SurveyNumberStepper';

/** Keys used for “days per week you work” (strip length + WFH max). API uses `days_per_week`. */
export const WORKING_DAYS_QUESTION_KEYS = ['days_per_week', 'working_days_per_week', 'work_days_per_week'];

const DAYS_PER_WEEK_KEYS = WORKING_DAYS_QUESTION_KEYS;

export function readWorkingDaysPerWeek(answers) {
  if (!answers || typeof answers !== 'object') return 5;
  for (const key of DAYS_PER_WEEK_KEYS) {
    const raw = answers[key];
    if (raw != null && raw !== '') {
      const n = Number(raw);
      if (Number.isFinite(n)) return Math.min(7, Math.max(1, n));
    }
  }
  return 5;
}

/** Saved WFH-day count from answers, or `null` if not set (do not treat as 0). */
export function readWfhDaysPerWeekAnswer(answers) {
  if (!answers || typeof answers !== 'object') return null;
  const raw = answers.wfh_days_per_week;
  if (raw === '' || raw == null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  const cap = readWorkingDaysPerWeek(answers);
  return Math.min(cap, Math.max(0, n));
}

const buildCanonicalWeek = (length, homeCount) =>
  Array.from({ length }, (_, i) => (i < homeCount ? 'home' : 'office'));

const WeekPattern = ({ week, onToggle }) => {
  const wfh = week.filter((d) => d === 'home').length;
  const office = week.length - wfh;
  return (
    <div className='pt-1'>
      <div className='flex items-center gap-1.5'>
        {week.map((kind, i) => (
          <button
            key={i}
            type='button'
            onClick={() => onToggle(i)}
            aria-label={`Day ${i + 1} is ${kind}. Tap to switch to ${kind === 'home' ? 'office' : 'home'}.`}
            className={`flex-1 h-14 rounded-lg flex flex-col items-center justify-center gap-0.5 border transition-all active:scale-[0.96] cursor-pointer ${
              kind === 'home'
                ? 'bg-primary/15 text-accent border-primary/30 hover:bg-primary/20'
                : 'bg-muted text-muted-foreground border-border hover:bg-muted/80 hover:border-muted-foreground/40'
            }`}>
            {kind === 'home' ? (
              <Home size={15} strokeWidth={1.75} />
            ) : (
              <Building2 size={15} strokeWidth={1.75} />
            )}
            <span className='text-[9px] font-mono uppercase tracking-[0.1em]'>{kind === 'home' ? 'home' : 'office'}</span>
          </button>
        ))}
      </div>
      <div className='flex items-center justify-between mt-1.5 text-[10px] text-muted-foreground font-mono uppercase tracking-[0.12em]'>
        <span>Tap a day to toggle</span>
        <span>
          {wfh} home · {office} office
        </span>
      </div>
    </div>
  );
};

/**
 * WFH days count with home/office strip (prototype WorkingPattern).
 * Reads `working_days_per_week` / `work_days_per_week` / `days_per_week` from answers for strip length; defaults to 5.
 *
 * The strip keeps its own tile order (like the prototype). Only the numeric stepper resets to “homes on the left”;
 * the saved answer is still just the count of home days, not which weekday is which.
 */
const WfhDaysPerWeekField = ({ value, onChange, answers = {} }) => {
  const daysPerWeek = readWorkingDaysPerWeek(answers);
  const wfhRaw = Number(value);
  const wfhDays = Number.isFinite(wfhRaw) ? Math.min(daysPerWeek, Math.max(0, wfhRaw)) : 0;

  const [week, setWeek] = useState(() => buildCanonicalWeek(daysPerWeek, wfhDays));

  useEffect(() => {
    if (!Number.isFinite(wfhRaw)) return;
    if (wfhRaw > daysPerWeek) onChange(daysPerWeek);
  }, [daysPerWeek, wfhRaw, onChange, value]);

  useEffect(() => {
    setWeek((prev) => {
      let next = prev;
      if (prev.length !== daysPerWeek) {
        if (prev.length < daysPerWeek) next = [...prev, ...Array(daysPerWeek - prev.length).fill('office')];
        else next = prev.slice(0, daysPerWeek);
      }
      const homes = next.filter((d) => d === 'home').length;
      if (homes !== wfhDays) return buildCanonicalWeek(daysPerWeek, wfhDays);
      return next;
    });
  }, [wfhDays, daysPerWeek]);

  const homeCount = week.filter((d) => d === 'home').length;

  const setWfhDays = (n) => {
    const clamped = Math.min(daysPerWeek, Math.max(0, n));
    setWeek(buildCanonicalWeek(daysPerWeek, clamped));
    onChange(clamped);
  };

  const toggleDay = (i) => {
    setWeek((prev) => {
      const next = prev.map((d, idx) => (idx === i ? (d === 'home' ? 'office' : 'home') : d));
      onChange(next.filter((d) => d === 'home').length);
      return next;
    });
  };

  const officeDays = daysPerWeek - homeCount;
  const mode = homeCount === 0 ? 'office' : homeCount === daysPerWeek ? 'home' : 'hybrid';

  return (
    <div className='space-y-3'>
      <SurveyNumberStepper value={homeCount} onChange={setWfhDays} min={0} max={daysPerWeek} />
      <WeekPattern week={week} onToggle={toggleDay} />

      {mode === 'office' ? (
        <Alert>
          <Info size={14} strokeWidth={1.5} />
          <AlertTitle>Always in the office</AlertTitle>
          <AlertDescription>
            We&apos;ll skip the home-office sections and go straight to your commute.
          </AlertDescription>
        </Alert>
      ) : null}

      {mode === 'home' ? (
        <Alert>
          <Info size={14} strokeWidth={1.5} />
          <AlertTitle>Fully remote</AlertTitle>
          <AlertDescription>
            We&apos;ll ask about your home setup and skip the commute section — no office travel to report.
          </AlertDescription>
        </Alert>
      ) : null}

      {mode === 'hybrid' ? (
        <Alert>
          <Info size={14} strokeWidth={1.5} />
          <AlertTitle>Hybrid pattern</AlertTitle>
          <AlertDescription>
            {homeCount} day{homeCount === 1 ? '' : 's'} from home and {officeDays} day{officeDays === 1 ? '' : 's'} in the
            office. We&apos;ll ask about both your home setup and your commute next.
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
};

export default WfhDaysPerWeekField;
